/**
 * AppShell — top-level layout used by every authenticated route.
 *
 * Renders the fixed sidebar on the left and a flex column on the right
 * containing the header + page content. Pages render their own header (via
 * `<Header crumbs=… actions=… />`) and their own tier selector / tab bar
 * inside the `<Outlet />`. The shell only owns the sidebar — it deliberately
 * does not own the header so each section can configure its own crumbs and
 * action buttons without prop drilling.
 */

import { Suspense, useEffect } from 'react';
import { Outlet } from 'react-router';
import { PharmacyModuleStrips } from './PharmacyModuleStrips';
import { Sidebar } from './Sidebar';
import { PageGuard } from './PageGuard';
import { ImpersonationBanner } from './ImpersonationBanner';
import { AssistBanner } from './AssistBanner';
// CHAT-001 (2026-06-11) — floating chat on every authenticated page.
import { ChatButton } from './ChatButton';
import { sendHeartbeat } from '../lib/meApi';
// TUTORIAL-STATE-001 (2026-09-21) — the first-login walk round the platform.
// Mounted here because this is the only wrapper that covers every signed-in
// route and nothing else: the login page, kiosk, driver tablet and the auditor,
// care-home and platform-admin areas all render outside the shell.
import { TutorialProvider } from '../tutorial/TutorialProvider';
import { PrintStamp } from '../components/PrintStamp';
import { useCompanyClock } from '../lib/companyClock';
import { useCompanyMoney } from '../lib/companyMoney';

/**
 * 2026-06-10 — interaction-aware presence (Shane). Sends a heartbeat to
 * POST /api/auth/heartbeat once a minute, but ONLY while the person is
 * genuinely using the app: a real input event (pointer / key / wheel /
 * touch) within the last 2 minutes AND the tab visible. A tab left open on
 * a second monitor goes quiet and the person drops off "Online" within
 * 5 minutes — the false-positive the old request-stamping approach had
 * (background polling kept idle tabs "Online"). Mounted once here so every
 * authenticated route is covered.
 */
const HEARTBEAT_INTERVAL_MS = 60 * 1000;
const INTERACTION_WINDOW_MS = 2 * 60 * 1000;

function useInteractionHeartbeat(): void {
  useEffect(() => {
    let lastInteraction = Date.now();
    const mark = (): void => {
      lastInteraction = Date.now();
    };
    const events: Array<keyof WindowEventMap> = [
      'pointerdown',
      'keydown',
      'wheel',
      'touchstart',
    ];
    for (const ev of events) {
      window.addEventListener(ev, mark, { passive: true });
    }

    const send = (): void => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastInteraction > INTERACTION_WINDOW_MS) return;
      void sendHeartbeat().catch(() => {
        /* presence is best-effort — never surface an error for it */
      });
    };

    send(); // arriving in the app counts as interacting
    const id = window.setInterval(send, HEARTBEAT_INTERVAL_MS);
    return () => {
      window.clearInterval(id);
      for (const ev of events) {
        window.removeEventListener(ev, mark);
      }
    };
  }, []);
}

export function AppShell(): JSX.Element {
  useInteractionHeartbeat();
  // Loads the company's time zone for every signed-in page, so code outside a
  // component (companyTodayIso) reads the company's date too (TODO 135).
  useCompanyClock();
  // The same for the company's currency, read by formatMoney outside a component.
  useCompanyMoney();
  return (
    <>
      {/*
        * EVERY PRINTED PAGE SAYS HOW OLD IT IS AND WHO TOOK IT.
        *
        * Shane, 2026-09-26, after deciding the platform itself is the
        * controlled document rather than a numbered register entry: *"we are
        * not doing the doc number on a printed document as before. We are
        * doing the printed by and date. in the top right corner and on the
        * bottom right we have powered by JeanieIQ on any print areas"*.
        *
        * MOUNTED ON THE SHELL, not on each screen. A dozen pages call
        * `window.print()` on themselves and more will follow; stamping them
        * one by one means the next one added is stamped only if somebody
        * remembers. Here it is automatic, and "any print areas" means any.
        *
        * Hidden on screen and takes no space — see `components/PrintStamp`.
        * The Fire Register's print view renders OUTSIDE this shell so the
        * sheet has no sidebar, and carries its own copy for that reason.
        */}
      <PrintStamp />
      <Sidebar />
      <div className="main">
        {/* JN-ADMIN-IMP-001 — visible only during an admin impersonation
            session (when the access token carries `imp`). Component
            returns null otherwise. */}
        <ImpersonationBanner />
        {/*
          * The other half of that banner, and the one that was missing: what the
          * TENANT sees. Shane, 2026-08-31: *"that is what we see now bit so
          * should they"*. Shown to every user here, not only the one who
          * approved it, and it carries the Disconnect.
          */}
        <AssistBanner />
        {/*
          BANNER-PLACEMENT-001 (2026-09-18) — moved into Header.

          It rendered HERE, before <Outlet />, and each page draws its own
          <Header> inside that outlet — so the warning sat ABOVE the header,
          over the breadcrumb and the account controls, as the first thing on
          the screen. Reagan wanted it below the header, and below is where a
          page-level notice belongs: the header is chrome, the notice is about
          the tenant.

          Header is the one component every page shares, so rendering it
          there puts it directly under the bar on every screen without each
          page having to remember.
        */}
        {/*
          PHARMACY-STRIPS (2026-09-28, showing Reagan option (iii)). On a
          pharmacy module that still belongs to a hub bank, the banks render as
          a top tab strip and the bank's modules as a strip down the left, with
          the routed page beside them. Every other route is untouched — the
          component returns its children unchanged when the path has no bank.

          The tutorial provider wraps the Outlet rather than sitting beside it,
          because the "Show me around" control lives in each page's own Header
          and reaches the running tour through this context. It goes OUTSIDE
          the strips, so a tour step can point at the bank tabs and the module
          strip as well as at the page they frame.
        */}
        <TutorialProvider>
          <PharmacyModuleStrips>
            <PageGuard>
              {/* The page's code loads on first open; the sidebar stays while it does. */}
              <Suspense fallback={null}>
                <Outlet />
              </Suspense>
            </PageGuard>
          </PharmacyModuleStrips>
        </TutorialProvider>
      </div>
      {/* CHAT-001 — fixed-position; mounting here puts it on every page. */}
      <ChatButton />
    </>
  );
}
